import moment, { type Moment } from 'moment';
import type { IntlShape } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { translateString } from '@src/util/intl/translateString';

export const ABSENCES_LIST_FILTER_FIELDS = {
  SEARCH: 'search',
  TYPE: 'type',
  DATE_RANGE: 'dateRange',
} as const;

export interface AbsencesListFilterState {
  search: string;
  type: string;
  dateRange: [Moment, Moment] | null;
}

export const INIT_ABSENCES_LIST_FILTERS: FilterValues = {
  [ABSENCES_LIST_FILTER_FIELDS.SEARCH]: {
    value: '',
    label: translateString('general.search.by.user'),
    valueType: 'string',
  },
  [ABSENCES_LIST_FILTER_FIELDS.TYPE]: {
    value: '',
    label: translateString('absences.type'),
    valueType: 'string',
  },
  [ABSENCES_LIST_FILTER_FIELDS.DATE_RANGE]: {
    value: [],
    label: translateString('general.range.dates'),
    valueType: 'array',
    clearValue: [],
  },
};

export const formatAbsenceDateRangeForTag = (dateRange: unknown): string => {
  if (!Array.isArray(dateRange) || dateRange.length !== 2) return '';

  const start = moment(dateRange[0]);
  const end = moment(dateRange[1]);

  if (!start.isValid() || !end.isValid()) return '';

  return `${start.format('L')} – ${end.format('L')}`;
};

export const filterValuesToAbsencesListFilters = (
  values: FilterValues,
): AbsencesListFilterState => {
  const search = String(values[ABSENCES_LIST_FILTER_FIELDS.SEARCH]?.value ?? '').trim();
  const type = String(values[ABSENCES_LIST_FILTER_FIELDS.TYPE]?.value ?? '');

  const rawDates = values[ABSENCES_LIST_FILTER_FIELDS.DATE_RANGE]?.value;
  let dateRange: [Moment, Moment] | null = null;

  if (Array.isArray(rawDates) && rawDates.length === 2 && rawDates[0] && rawDates[1]) {
    dateRange = [moment(rawDates[0]), moment(rawDates[1])];
  }

  return { search, type, dateRange };
};

export const buildAbsencesListFilterSetValueProps = (
  key: string,
  value: unknown,
  intl: IntlShape,
  valueTranslated?: string,
): SetValueProps => {
  switch (key) {
    case ABSENCES_LIST_FILTER_FIELDS.SEARCH:
      return {
        key,
        value: String(value ?? ''),
        label: intl.formatMessage({ id: 'general.search.by.user' }),
        valueTranslated: String(value ?? ''),
        valueType: 'string',
      };
    case ABSENCES_LIST_FILTER_FIELDS.TYPE:
      return {
        key,
        value: String(value ?? ''),
        label: intl.formatMessage({ id: 'absences.type' }),
        valueTranslated: valueTranslated ?? String(value ?? ''),
        valueType: 'string',
      };
    case ABSENCES_LIST_FILTER_FIELDS.DATE_RANGE: {
      const serializedDates = Array.isArray(value)
        ? value
            .filter(Boolean)
            .slice(0, 2)
            .map(entry => moment(entry).toISOString())
        : [];

      return {
        key,
        value: serializedDates,
        label: intl.formatMessage({ id: 'general.range.dates' }),
        valueTranslated: formatAbsenceDateRangeForTag(serializedDates),
        valueType: 'array',
        clearValue: [],
      };
    }
    default:
      return { key, value: value as SetValueProps['value'], valueType: 'string' };
  }
};
